import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm, useGuardedMultiEdit } from '../../../../hooks';
import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';
import { multiFieldsUtil, SAVE_TO_PARENT_KEY } from '../../../../utils/postingUtils';
import typeCheckUtils from '../../../../utils/typeCheckUtils';

import { bedOptions } from '../../../../constants/ListingDetailsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import FloorPlan from '../../../../model/FloorPlan';
import WizardServerError from '../../../../model/WizardServerError';

import { WizardContext } from '../../../../components/Wizard/context';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';
import type { MockEventProps } from '../types';

const inputName = 'beds';
const multiEndpointFieldName = 'numBedrooms';

const BedroomsFormField = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, isSaveComplete, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { floorplans, listingAlias } = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const floorplan = floorplans[0] || {};
  const { beds } = floorplan;

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      beds,
    },
    postingConstraints,
  );
  const { saveMultipleFieldsPropertyInfo } = useGuardedMultiEdit();

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: 'beds',
          // @ts-expect-error
          value: inputs.beds,
        },
      });
    }
  }, [isSaveComplete]);

  let bedOptionsValues = bedOptions;

  if (beds !== '') {
    bedOptionsValues = bedOptions.slice(1);
  }

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLSelectElement> | MockEventProps) => {
    const { value, name: fieldName } = event.target;

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const numCastValue = typeCheckUtils.Number(value);

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName: multiEndpointFieldName,
      floorPlanId: floorplan?.floorPlanId,
      listingAlias,
      saveToParentKey: SAVE_TO_PARENT_KEY.FLOOR_PLANS,
      value: numCastValue,
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        // @ts-expect-error guard can return void
        const { listingOutputFieldData } = response;
        // all data properties are not returned in the response
        const mergedFloorPlan = {
          ...floorplans[0],
          ...listingOutputFieldData?.floorPlans[0],
        };

        // TODO: BE needs to sync responses on various endpoints beds, numBedrooms
        // sync mismatched keys from response
        const floorPlan = FloorPlan(mergedFloorPlan);
        floorPlan.beds = mergedFloorPlan?.numBedrooms ?? '';
        floorPlan.baths = mergedFloorPlan?.numBathrooms ?? '';

        // if squareFeet is null the key is not returned in the response
        floorPlan['squareFeet'] = String(listingOutputFieldData?.floorPlans[0]?.squareFeet ?? '');

        // make sure we pass an array
        dispatch(PropertiesActions.updateCurrentProperty({ floorplans: [floorPlan] }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        // this will always display at the page level
        // the field level server errors should be handle in review page modal
        displayAt: DISPLAY_SERVER_ERROR_AT.PAGE,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <FormField
      data-testid="BedroomsFormField"
      label={<Label required={true}>Total bedrooms</Label>}
      control={
        <Select
          size={isScreenDesktop ? 'lg' : 'md'}
          aria-label="Total bedrooms"
          name={inputName}
          value={String(inputs.beds ?? '')}
          onChange={handleChange}
          onBlur={save}
          required={true}
          // @ts-expect-error - error prop is not yet typed in v10
          error={!!errorObj.beds}
          css={{ whiteSpace: 'pre' }}
        >
          {bedOptionsValues.map((option) => {
            if (option === 'studio') {
              return (
                <option key={option} value={0}>
                  {option}
                </option>
              );
            }
            return (
              <option key={option} value={option}>
                {option}
              </option>
            );
          })}
        </Select>
      }
      // @ts-expect-error
      description={<FormHelp error={errorObj.beds}>{errorObj.beds}</FormHelp>}
    />
  );
};

export default BedroomsFormField;
